/**
 * The Phase 5D operations: security intelligence — the organization's
 * overview, findings across repositories and component inventory; a
 * finding's history, deterministic remediation guidance and engineering
 * context; and the remediation claim.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type SecurityOverview = Schemas['SecurityOverview'];
export type SecurityPriority = Schemas['SecurityPriority'];
export type SecurityVerificationState = Schemas['SecurityVerificationState'];
export type SecurityPriorityAssessment = Schemas['SecurityPriorityAssessment'];
export type SecurityPriorityFactor = Schemas['SecurityPriorityFactor'];
export type SecurityIntelligence = Schemas['SecurityIntelligence'];
export type SecurityVerification = Schemas['SecurityVerification'];
export type SecurityFindingEvent = Schemas['SecurityFindingEvent'];
export type SecurityRemediationGuidance = Schemas['SecurityRemediationGuidance'];
export type SecurityFindingContext = Schemas['SecurityFindingContext'];
export type SecurityComponent = Schemas['SecurityComponent'];
export type SecurityRemediationClaim = Schemas['SecurityRemediationClaim'];
export type SecurityFindingCategory = Schemas['SecurityFindingCategory'];

export interface Paged5D<T> {
  data: T[];
  next_cursor: string | null;
}

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): Paged5D<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

export function getSecurityOverview(orgId: string): Promise<SecurityOverview> {
  return expect(
    http.GET('/organizations/{org_id}/security/overview', { params: { path: { org_id: orgId } } }),
  );
}

export interface OrgFindingsQuery {
  status?: (Schemas['SecurityFindingStatus'] | 'all')[];
  severity?: Schemas['SecuritySeverity'][];
  priority?: SecurityPriority[];
  category?: Schemas['SecurityFindingCategory'][];
  verification?: SecurityVerificationState[];
  repository_id?: string;
  assignee_id?: string;
  scanner?: string;
  package?: string;
  overdue?: boolean;
  limit?: number;
  cursor?: string;
}

export async function listOrgSecurityFindings(
  orgId: string,
  query: OrgFindingsQuery = {},
): Promise<Paged5D<Schemas['SecurityFinding']>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/security/findings', {
      params: { path: { org_id: orgId }, query },
    }),
  );
  return paged<Schemas['SecurityFinding']>(value);
}

export async function listSecurityComponents(
  orgId: string,
  query: {
    ecosystem?: string;
    name?: string;
    namespace?: string;
    version?: string;
    repository_id?: string;
    limit?: number;
    cursor?: string;
  },
): Promise<Paged5D<SecurityComponent>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/security/components', {
      params: { path: { org_id: orgId }, query },
    }),
  );
  return paged<SecurityComponent>(value);
}

export function getRepositorySecurityFinding(
  scope: RepoScope,
  findingId: string,
): Promise<Schemas['SecurityFinding']> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}', {
      params: { path: { ...repoPath(scope), finding_id: findingId } },
    }),
  );
}

export async function getSecurityFindingHistory(
  scope: RepoScope,
  findingId: string,
): Promise<Paged5D<SecurityFindingEvent>> {
  const value = await expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/history',
      { params: { path: { ...repoPath(scope), finding_id: findingId } } },
    ),
  );
  return paged<SecurityFindingEvent>(value);
}

export function getSecurityFindingRemediation(
  scope: RepoScope,
  findingId: string,
): Promise<SecurityRemediationGuidance> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/remediation',
      { params: { path: { ...repoPath(scope), finding_id: findingId } } },
    ),
  );
}

export function getSecurityFindingContext(
  scope: RepoScope,
  findingId: string,
): Promise<SecurityFindingContext> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/context',
      { params: { path: { ...repoPath(scope), finding_id: findingId } } },
    ),
  );
}

export function claimSecurityFindingRemediation(
  scope: RepoScope,
  findingId: string,
  body: SecurityRemediationClaim,
): Promise<Schemas['SecurityFinding']> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/remediation',
      { params: { path: { ...repoPath(scope), finding_id: findingId } }, body },
    ),
  );
}
